import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  TabContent, 
  TabPane, 
  Table, 
  Button, 
  Input, 
  FormGroup, 
  Label, 
  Modal, 
  ModalHeader, 
  ModalBody,
  Badge,
  Spinner,
  UncontrolledTooltip
} from "reactstrap";
import { AdminService } from "../../services/AdminService";
import { Therapist } from "../../models/Therapist";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { format } from "date-fns";
import "../../assets/css/admin.css";

interface TrainingStatus {
  therapistId: string;
  status: 'not_sent' | 'sent' | 'completed';
  linkCreatedAt?: Date;
  emailSentAt?: Date;
  completedAt?: Date;
  trainingUrl?: string;
  lastActivity?: Date;
  signatureData?: string;
  signedAt?: Date;
  signerName?: string;
}

interface TrainingLog {
  id: string;
  therapistId: string;
  therapistName: string;
  linkCreatedAt?: Date;
  firstOpenedAt?: Date;
  lastOpenedAt?: Date;
  watchTime: number; // in seconds
  thresholdMet: boolean;
  lastEvent: string;
  events: TrainingEvent[];
  signatureData?: string;
  signedAt?: Date;
  signerName?: string;
}

interface TrainingEvent {
  id: string;
  type: 'link_opened' | 'playback_start' | 'pause' | 'resume' | 'exit' | 'playback_complete';
  timestamp: Date;
  sessionDuration?: number;
  metadata?: any;
}

const ClinicalTrainingVideoManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'generate' | 'logs'>('generate');
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [trainingStatuses, setTrainingStatuses] = useState<TrainingStatus[]>([]);
  const [trainingLogs, setTrainingLogs] = useState<TrainingLog[]>([]);
  const [selectedTherapists, setSelectedTherapists] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [generateSearchTerm, setGenerateSearchTerm] = useState("");
  const [logsSearchTerm, setLogsSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<'all' | 'not_sent' | 'sent' | 'completed'>('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedLogEvents, setSelectedLogEvents] = useState<TrainingEvent[]>([]);
  const [selectedLogTherapist, setSelectedLogTherapist] = useState('');
  
  // Signature modal states
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState<{signature: string, signerName: string, signedAt: Date} | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Re-fetch data when switching tabs
  useEffect(() => {
    if (activeTab === 'logs') {
      fetchTrainingLogs();
    } else if (activeTab === 'generate') {
      fetchTherapists();
      fetchTrainingStatuses();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        fetchTherapists(),
        fetchTrainingStatuses(),
        fetchTrainingLogs()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTherapists = async () => {
    try {
      const response = await AdminService.getAllTherapistsList();
      setTherapists(response.data || []);
    } catch (error) {
      console.error('Error fetching therapists:', error);
      toast.error('Failed to fetch therapists');
    }
  };

  const fetchTrainingStatuses = async () => {
    try {
      const response = await AdminService.getTrainingStatuses();
      setTrainingStatuses(response.data || []);
    } catch (error) {
      console.error('Error fetching training statuses:', error);
      // Don't show error toast for this as it might be expected during initial development
    }
  };

  const fetchTrainingLogs = async () => {
    try {
      const filters = {
        therapistName: logsSearchTerm || undefined
      };
      const response = await AdminService.getTrainingLogs(filters);
      setTrainingLogs(response.data || []);
    } catch (error) {
      console.error('Error fetching training logs:', error);
      // Don't show error toast for this as it might be expected during initial development
    }
  };

  const generateAndSendTraining = async (therapistIds: string[], isResend: boolean = false) => {
    try {
      setIsSending(true);
      
      const result = await Swal.fire({
        title: isResend ? 'Re-send Training Links?' : 'Generate & Send Training Links?',
        text: `This will ${isResend ? 'resend' : 'generate and send'} clinical training links to ${therapistIds.length} therapist(s).`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: isResend ? 'Re-send' : 'Generate & Send',
        cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) {
        setIsSending(false);
        return;
      }

      // API call to generate and send training links
      await AdminService.generateTrainingLinks({
        therapistIds,
        isResend
      });

      toast.success(`Successfully ${isResend ? 'resent' : 'generated and sent'} training links to ${therapistIds.length} therapist(s)`);
      
      // Refresh data
      await fetchTrainingStatuses();
      setSelectedTherapists([]);
      
    } catch (error) {
      console.error('Error generating/sending training links:', error);
      toast.error('Failed to generate/send training links');
    } finally {
      setIsSending(false);
    }
  };

  const handleBulkAction = () => {
    if (selectedTherapists.length === 0) {
      toast.warning('Please select therapists first');
      return;
    }
    generateAndSendTraining(selectedTherapists);
  };

  const handleSingleAction = (therapistId: string, isResend: boolean = false) => {
    generateAndSendTraining([therapistId], isResend);
  };

  const toggleTherapistSelection = (therapistId: string) => {
    setSelectedTherapists(prev => 
      prev.includes(therapistId) 
        ? prev.filter(id => id !== therapistId)
        : [...prev, therapistId]
    );
  };

  const selectAllTherapists = () => {
    const filteredTherapists = getFilteredTherapists();
    const allSelected = filteredTherapists.every(t => selectedTherapists.includes(t._id));
    
    if (allSelected) {
      setSelectedTherapists([]);
    } else {
      setSelectedTherapists(filteredTherapists.map(t => t._id));
    }
  };

  const getTherapistStatus = (therapistId: string): TrainingStatus => {
    return trainingStatuses.find(s => s.therapistId === therapistId) || {
      therapistId,
      status: 'not_sent'
    };
  };

  const getFilteredTherapists = () => {
    return therapists.filter(therapist => {
      const matchesSearch = therapist.firstname?.toLowerCase().includes(generateSearchTerm.toLowerCase()) ||
                           therapist.lastname?.toLowerCase().includes(generateSearchTerm.toLowerCase()) ||
                           therapist.email?.toLowerCase().includes(generateSearchTerm.toLowerCase());
      
      const status = getTherapistStatus(therapist._id);
      const matchesStatus = statusFilter === 'all' || status.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  };

  const getFilteredLogs = () => {
    return trainingLogs.filter(log => {
      const matchesSearch = log.therapistName?.toLowerCase().includes(logsSearchTerm.toLowerCase());
      return matchesSearch;
    });
  };

  const showEventTimeline = (log: TrainingLog) => {
    setSelectedLogEvents(log.events || []);
    setSelectedLogTherapist(log.therapistName);
    setShowEventModal(true);
  };

  const showSignature = (signatureData: string, signerName: string, signedAt: Date) => {
    setSelectedSignature({ signature: signatureData, signerName, signedAt });
    setShowSignatureModal(true);
  };

  const renderSignatureCell = (status: TrainingStatus) => {
    if (status.signatureData && status.signerName && status.signedAt) {
      return (
        <Button
          color="success"
          size="sm"
          onClick={() => showSignature(status.signatureData!, status.signerName!, status.signedAt!)}
          className="d-flex align-items-center gap-1"
        >
          <i className="mdi mdi-file-document-outline"></i>
          View
        </Button>
      );
    }
    return <span className="text-muted">No signature</span>;
  };

  const renderLogSignatureCell = (log: TrainingLog) => {
    if (log.signatureData && log.signerName && log.signedAt) {
      return (
        <Button
          color="success"
          size="sm"
          onClick={() => showSignature(log.signatureData!, log.signerName!, log.signedAt!)}
          className="d-flex align-items-center gap-1"
        >
          <i className="mdi mdi-file-document-outline"></i>
          View
        </Button>
      );
    }
    return <span className="text-muted">No signature</span>;
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'not_sent':
        return <Badge color="secondary">Not Sent</Badge>;
      case 'sent':
        return <Badge color="warning">Sent</Badge>;
      case 'completed':
        return <Badge color="success">Completed</Badge>;
      default:
        return <Badge color="secondary">Unknown</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center mt-5">
            <Spinner color="primary" size="lg" />
            <p className="mt-2">Loading training management data...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Clinical Training Video" breadcrumbItem="Management" />
        
        <Row>
          <Col>
            <Card>
              <CardBody>
                {/* Navigation Tabs */}
                <div className="custom-tabs-container mb-4">
                  <div className="custom-tabs-wrapper">
                    <div
                      className={`custom-tab ${activeTab === 'generate' ? 'active' : ''}`}
                      onClick={() => setActiveTab('generate')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="tab-text">
                        <i className="mdi mdi-send me-2"></i>
                        Generate & Send
                      </span>
                      {activeTab === 'generate' && <div className="tab-underline"></div>}
                    </div>

                    <div
                      className={`custom-tab ${activeTab === 'logs' ? 'active' : ''}`}
                      onClick={() => setActiveTab('logs')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="tab-text">
                        <i className="mdi mdi-chart-timeline-variant me-2"></i>
                        Logs
                      </span>
                      {activeTab === 'logs' && <div className="tab-underline"></div>}
                    </div>
                  </div>
                </div>

                <TabContent activeTab={activeTab} className="mt-3">
                  {/* Generate & Send Tab */}
                  <TabPane tabId="generate">
                    <Row className="mb-3">
                      <Col md={6}>
                        <FormGroup>
                          <Label>Search Therapists</Label>
                          <Input
                            type="text"
                            placeholder="Search by name or email..."
                            value={generateSearchTerm}
                            onChange={(e) => setGenerateSearchTerm(e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Filter by Status</Label>
                          <Input
                            type="select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as any)}
                          >
                            <option value="all">All</option>
                            <option value="not_sent">Not Sent</option>
                            <option value="sent">Sent</option>
                            <option value="completed">Completed</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>&nbsp;</Label>
                          <div>
                            <Button
                              color="primary"
                              disabled={selectedTherapists.length === 0 || isSending}
                              onClick={handleBulkAction}
                              block
                            >
                              {isSending ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-send me-1"></i>}
                              Send to Selected ({selectedTherapists.length})
                            </Button>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="table-responsive">
                      <Table className="table table-bordered mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: '40px' }}>
                              <input
                                type="checkbox"
                                checked={getFilteredTherapists().length > 0 && 
                                         getFilteredTherapists().every(t => selectedTherapists.includes(t._id))}
                                onClick={(e) => {
                                  e.preventDefault();
                                  selectAllTherapists();
                                }}
                                readOnly
                              />
                            </th>
                            <th>Therapist Name</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Signature</th>
                            <th style={{ width: '120px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getFilteredTherapists().map(therapist => {
                            const status = getTherapistStatus(therapist._id);
                            return (
                              <tr key={therapist._id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedTherapists.includes(therapist._id)}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      toggleTherapistSelection(therapist._id);
                                    }}
                                    readOnly
                                  />
                                </td>
                                <td>{`${therapist.firstname || ''} ${therapist.lastname || ''}`.trim()}</td>
                                <td>{therapist.email}</td>
                                <td>{getStatusBadge(status.status)}</td>
                                <td>{renderSignatureCell(status)}</td>
                                <td>
                                  {status.status === 'not_sent' ? (
                                    <Button
                                      color="primary"
                                      size="sm"
                                      disabled={isSending}
                                      onClick={() => handleSingleAction(therapist._id)}
                                    >
                                      <i className="mdi mdi-send"></i>
                                    </Button>
                                  ) : (
                                    <Button
                                      color="warning"
                                      size="sm"
                                      disabled={isSending}
                                      onClick={() => handleSingleAction(therapist._id, true)}
                                      id={`resend-${therapist._id}`}
                                    >
                                      <i className="mdi mdi-refresh"></i>
                                      <UncontrolledTooltip target={`resend-${therapist._id}`}>
                                        Re-send training link
                                      </UncontrolledTooltip>
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>

                    {getFilteredTherapists().length === 0 && (
                      <div className="text-center py-4">
                        <i className="mdi mdi-account-search display-4 text-muted"></i>
                        <p className="text-muted mt-2">No therapists found</p>
                      </div>
                    )}
                  </TabPane>

                  {/* Logs Tab */}
                  <TabPane tabId="logs">
                    <Row className="mb-3">
                      <Col md={8}>
                        <FormGroup>
                          <Label>Search Therapists</Label>
                          <Input
                            type="text"
                            placeholder="Search by therapist name..."
                            value={logsSearchTerm}
                            onChange={(e) => setLogsSearchTerm(e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>&nbsp;</Label>
                          <div>
                            <Button
                              color="info"
                              onClick={fetchTrainingLogs}
                              block
                            >
                              <i className="mdi mdi-refresh me-1"></i>
                              Refresh
                            </Button>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="table-responsive">
                      <Table className="table table-bordered mb-0">
                        <thead>
                          <tr>
                            <th>Therapist</th>
                            <th>First Opened At</th>
                            <th>Last Opened At</th>
                            <th>Last Event</th>
                            <th>Signature</th>
                            <th style={{ width: '100px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getFilteredLogs().map(log => (
                            <tr key={log.id}>
                              <td>{log.therapistName}</td>
                              <td>
                                {log.firstOpenedAt 
                                  ? format(new Date(log.firstOpenedAt), 'MMM dd, yyyy HH:mm')
                                  : '-'
                                }
                              </td>
                              <td>
                                {log.lastOpenedAt 
                                  ? format(new Date(log.lastOpenedAt), 'MMM dd, yyyy HH:mm')
                                  : '-'
                                }
                              </td>
                              <td>
                                <Badge color="info" className="text-capitalize">
                                  {log.lastEvent?.replace('_', ' ')}
                                </Badge>
                              </td>
                              <td>{renderLogSignatureCell(log)}</td>
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => showEventTimeline(log)}
                                >
                                  <i className="mdi mdi-timeline"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {getFilteredLogs().length === 0 && (
                      <div className="text-center py-4">
                        <i className="mdi mdi-chart-timeline-variant display-4 text-muted"></i>
                        <p className="text-muted mt-2">No training logs found</p>
                      </div>
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Event Timeline Modal */}
        <Modal isOpen={showEventModal} toggle={() => setShowEventModal(false)} size="lg">
          <ModalHeader toggle={() => setShowEventModal(false)}>
            Event Timeline - {selectedLogTherapist}
          </ModalHeader>
          <ModalBody>
            <div className="timeline-container">
              {selectedLogEvents.map((event, index) => (
                <div key={event.id} className="timeline-item d-flex mb-3">
                  <div className="timeline-marker">
                    <div className={`timeline-icon bg-${getEventColor(event.type)}`}>
                      <i className={`mdi ${getEventIcon(event.type)}`}></i>
                    </div>
                    {index < selectedLogEvents.length - 1 && <div className="timeline-line"></div>}
                  </div>
                  <div className="timeline-content ms-3">
                    <h6 className="timeline-title text-capitalize">
                      {event.type.replace('_', ' ')}
                    </h6>
                    <p className="timeline-time text-muted mb-0">
                      {format(new Date(event.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </ModalBody>
        </Modal>

        {/* Signature Modal */}
        <Modal isOpen={showSignatureModal} toggle={() => setShowSignatureModal(false)} size="md">
          <ModalHeader toggle={() => setShowSignatureModal(false)}>
            Training Completion Signature
          </ModalHeader>
          <ModalBody>
            {selectedSignature && (
              <div className="signature-display text-center">
                <div className="mb-3">
                  <strong>Signer:</strong> {selectedSignature.signerName}
                </div>
                <div className="mb-3">
                  <strong>Signed At:</strong> {format(new Date(selectedSignature.signedAt), 'MMM dd, yyyy HH:mm:ss')}
                </div>
                <div className="signature-image-container">
                  <img 
                    src={selectedSignature.signature} 
                    alt="Training completion signature" 
                    style={{ 
                      maxWidth: '100%', 
                      height: 'auto', 
                      border: '2px solid #e9ecef', 
                      borderRadius: '8px',
                      backgroundColor: '#fff'
                    }} 
                  />
                </div>
              </div>
            )}
          </ModalBody>
        </Modal>
      </Container>

      <style>{`
        .custom-tabs-container {
          border-bottom: 1px solid #dee2e6;
        }
        
        .custom-tabs-wrapper {
          display: flex;
          gap: 0;
        }
        
        .custom-tab {
          position: relative;
          padding: 15px 30px;
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          border-bottom: none;
          color: #6c757d;
          font-weight: 500;
          transition: all 0.3s ease;
          margin-right: 4px;
          border-radius: 8px 8px 0 0;
        }
        
        .custom-tab:hover {
          background-color: #e9ecef;
          color: #495057;
        }
        
        .custom-tab.active {
          background-color: #fff;
          color: #FD7F00;
          border-color: #dee2e6;
          z-index: 1;
        }
        
        .custom-tab.active .tab-underline {
          position: absolute;
          bottom: -1px;
          left: 0;
          right: 0;
          height: 3px;
          background-color: #FD7F00;
        }
        
        .tab-text {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .timeline-container {
          position: relative;
        }
        
        .timeline-item {
          position: relative;
        }
        
        .timeline-marker {
          position: relative;
          width: 40px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .timeline-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          z-index: 1;
        }
        
        .timeline-line {
          width: 2px;
          height: 40px;
          background-color: #e9ecef;
          margin-top: 8px;
        }
        
        .bg-primary { background-color: #556ee6 !important; }
        .bg-success { background-color: #34c38f !important; }
        .bg-warning { background-color: #f1b44c !important; }
        .bg-danger { background-color: #f46a6a !important; }
        .bg-info { background-color: #50a5f1 !important; }
      `}</style>
    </div>
  );
};

// Helper functions
const getEventColor = (eventType: string) => {
  switch (eventType) {
    case 'link_opened': return 'primary';
    case 'playback_start': return 'success';
    case 'pause': return 'warning';
    case 'resume': return 'success';
    case 'exit': return 'danger';
    case 'playback_complete': return 'success';
    default: return 'info';
  }
};

const getEventIcon = (eventType: string) => {
  switch (eventType) {
    case 'link_opened': return 'mdi-open-in-new';
    case 'playback_start': return 'mdi-play';
    case 'pause': return 'mdi-pause';
    case 'resume': return 'mdi-play';
    case 'exit': return 'mdi-exit-to-app';
    case 'playback_complete': return 'mdi-check-circle';
    default: return 'mdi-information';
  }
};

export default ClinicalTrainingVideoManagement;